import React, { useState } from "react";
import { Button } from "./ui/button";
import { FileDown, Printer } from "lucide-react";
import { toast } from "sonner";
import { extractError } from "../lib/api";
import { downloadApiFile, openApiPrintView } from "../lib/downloadApiFile";

const GROUP_SHORT = {
  Material: "Mat.",
  Freight: "Frt.",
  Sales: "Sales",
};

/**
 * Shared Print + PDF download actions — consistent layout across all document types.
 */
export default function DocumentPrintPdfActions({
  printPath,
  pdfPath,
  pdfFilename,
  groupLabel,
  printLabel = "Print",
  pdfLabel = "PDF",
  compact = false,
  printTitle,
  pdfTitle,
  testIdPrefix = "doc",
  stopPropagation = false,
  onPrint,
  onPdf,
  pdfSuccessMessage = "PDF downloaded",
}) {
  const [printing, setPrinting] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const wrapEvent = (handler) => async (e) => {
    if (stopPropagation) e?.stopPropagation?.();
    await handler(e);
  };

  const handlePrint = wrapEvent(async () => {
    if (onPrint) {
      await onPrint();
      return;
    }
    setPrinting(true);
    try {
      await openApiPrintView(printPath);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setPrinting(false);
    }
  });

  const handlePdf = wrapEvent(async () => {
    if (onPdf) {
      await onPdf();
      return;
    }
    setPdfBusy(true);
    try {
      await downloadApiFile(pdfPath, pdfFilename);
      toast.success(pdfSuccessMessage);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setPdfBusy(false);
    }
  });

  const resolvedPrintTitle = printTitle || printLabel;
  const resolvedPdfTitle = pdfTitle || `Download ${pdfLabel}`;

  if (compact) {
    return (
      <div
        className="flex items-center justify-end gap-0.5 shrink-0"
        onClick={stopPropagation ? (e) => e.stopPropagation() : undefined}
        onKeyDown={stopPropagation ? (e) => e.stopPropagation() : undefined}
        role="presentation"
      >
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
          onClick={handlePrint}
          disabled={printing}
          title={resolvedPrintTitle}
          aria-label={resolvedPrintTitle}
          data-testid={`print-${testIdPrefix}`}
        >
          <Printer className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
          onClick={handlePdf}
          disabled={pdfBusy}
          title={resolvedPdfTitle}
          aria-label={resolvedPdfTitle}
          data-testid={`pdf-${testIdPrefix}`}
        >
          <FileDown className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  const actions = (
    <div className="flex flex-wrap items-center gap-1.5 shrink-0">
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="text-xs h-8 shrink-0 whitespace-nowrap px-2.5"
        onClick={handlePrint}
        disabled={printing}
        title={resolvedPrintTitle}
        data-testid={`print-${testIdPrefix}`}
      >
        <Printer className="h-3 w-3 shrink-0 sm:mr-1" />
        <span className="hidden sm:inline">{printing ? "Opening…" : printLabel}</span>
      </Button>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="text-xs h-8 shrink-0 whitespace-nowrap px-2.5"
        onClick={handlePdf}
        disabled={pdfBusy}
        title={resolvedPdfTitle}
        data-testid={`pdf-${testIdPrefix}`}
      >
        <FileDown className="h-3 w-3 shrink-0 sm:mr-1" />
        <span className="hidden sm:inline">{pdfBusy ? "Exporting…" : pdfLabel}</span>
      </Button>
    </div>
  );

  if (!groupLabel) {
    return <div className="flex items-center gap-2 shrink-0">{actions}</div>;
  }

  const shortLabel = GROUP_SHORT[groupLabel] || groupLabel;

  return (
    <div className="flex flex-col gap-1 shrink-0 min-w-[5.5rem]">
      <span
        className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium leading-tight"
        title={groupLabel}
      >
        <span className="sm:hidden">{shortLabel}</span>
        <span className="hidden sm:inline">{groupLabel}</span>
      </span>
      {actions}
    </div>
  );
}
